import React, { useState } from 'react';
import { Pressable, type PressableProps, type StyleProp, type View, type ViewStyle } from 'react-native';

import { Box, Text, useTheme, type Theme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';

export type TabItemState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface TabItemProps extends Omit<
  PressableProps,
  'children' | 'style' | 'accessibilityRole' | 'accessibilityState'
> {
  label: string;
  selected?: boolean;
  width?: 'hug' | 'fill';
  badge?: string | number;
  /** Preview an interaction state. Disabled always suppresses activation. */
  state?: TabItemState;
  style?: StyleProp<ViewStyle>;
  ref?: React.Ref<View>;
}

function getTabStyle({
  theme,
  width,
  disabled,
  pressed,
  hovered,
  state,
}: {
  theme: Theme;
  width: 'hug' | 'fill';
  disabled: boolean;
  pressed: boolean;
  hovered: boolean;
  state: TabItemState;
}): ViewStyle {
  const showPressed = !disabled && (pressed || state === 'pressed');
  const showHover = !disabled && (hovered || state === 'hover');
  return {
    minWidth: 0,
    flexGrow: width === 'fill' ? 1 : 0,
    flexShrink: 1,
    flexBasis: width === 'fill' ? 0 : 'auto',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing[width === 'fill' ? 'spacing.general.x3' : 'spacing.general.0'],
    paddingVertical: theme.spacing['spacing.general.x4'],
    borderRadius: theme.borderRadii['radius.none'],
    backgroundColor: showPressed
      ? theme.colors['state-layer.press-brand']
      : showHover
        ? theme.colors['state-layer.hover-brand']
        : 'transparent',
  };
}

/** The current Figma .horizontal tab item/Squared family, shared by both orientations. */
export function TabItem({
  label,
  selected = false,
  width = 'hug',
  badge,
  state = 'enabled',
  disabled = false,
  style,
  ref,
  accessibilityLabel,
  onFocus,
  onBlur,
  onHoverIn,
  onHoverOut,
  ...props
}: TabItemProps) {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isDisabled = disabled || state === 'disabled';
  const showFocus = !isDisabled && (focused || state === 'focus');

  return (
    <Pressable
      {...props}
      ref={ref}
      accessibilityRole="tab"
      accessibilityLabel={accessibilityLabel ?? (badge !== undefined ? `${label}, ${badge}` : label)}
      accessibilityState={{ selected, disabled: isDisabled }}
      aria-selected={selected}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      onFocus={(event) => {
        setFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        onBlur?.(event);
      }}
      onHoverIn={(event) => {
        setHovered(true);
        onHoverIn?.(event);
      }}
      onHoverOut={(event) => {
        setHovered(false);
        onHoverOut?.(event);
      }}
      style={({ pressed }) => [getTabStyle({ theme, width, disabled: isDisabled, pressed, hovered, state }), style]}
    >
      <TabItemContent label={label} selected={selected} badge={badge} disabled={isDisabled} focused={showFocus} />
    </Pressable>
  );
}

function TabItemContent({
  label,
  selected,
  badge,
  disabled,
  focused,
}: {
  label: string;
  selected: boolean;
  badge?: string | number;
  disabled: boolean;
  focused: boolean;
}) {
  const theme = useTheme();
  const badgeSize = Number(theme.tokens.sizing['sizing.general.x5']);
  const hasBadge = badge !== undefined;
  // The shared theme retains `normal` line heights for legacy screens; use the exported Figma ratio here.
  const labelLineHeight = Math.round(
    Number(theme.tokens.typography['typography.font-size.body.base']) * INTERACTION.labelLineHeight,
  );
  const badgeLineHeight = Math.round(
    Number(theme.tokens.typography['typography.font-size.body.x-small']) * INTERACTION.labelLineHeight,
  );

  return (
    <>
      <Box flexDirection="row" alignItems="center" justifyContent="center" gap="spacing.general.x2">
        <Text
          variant={selected ? 'bodyBaseStrong' : 'bodyBaseMedium'}
          color={disabled ? 'text.disabled' : selected ? 'text.primary' : 'text.secondary'}
          flexShrink={1}
          textAlign="center"
          lineHeight={labelLineHeight}
        >
          {label}
        </Text>
        {hasBadge ? (
          <Box
            minHeight={badgeSize}
            minWidth={badgeSize}
            paddingHorizontal="spacing.general.x1"
            borderRadius="radius.full"
            backgroundColor={disabled ? 'surface.disabled' : 'surface.brand.default'}
            alignItems="center"
            justifyContent="center"
          >
            <Text
              variant="bodyXSmallMedium"
              lineHeight={badgeLineHeight}
              color={disabled ? 'text.disabled' : 'text.inverse.primary'}
            >
              {badge}
            </Text>
          </Box>
        ) : null}
      </Box>
      {selected ? (
        <Box
          pointerEvents="none"
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height={theme.borderWidths['border-width.emphasized']}
          backgroundColor={disabled ? 'text.disabled' : 'border.strong'}
        />
      ) : null}
      {focused ? (
        <Box
          pointerEvents="none"
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          borderWidth={theme.borderWidths['border-width.emphasized']}
          borderColor="border.focused"
        />
      ) : null}
    </>
  );
}
